import { createContext, useContext } from "react";

export type SessionActivityContextValue = {
    /** Marks the current tab (and, via cross-tab sync, all tabs) as active, resetting the idle countdown. */
    heartbeat: () => void;
    /** True while the idle warning should be shown, before a forced logout. */
    isPrompted: boolean;
    /** Milliseconds remaining before the idle timeout expires. */
    getRemainingTime: () => number;
    /** True when a SessionActivityProvider is actively wired up (the `session` config is present). */
    isEnabled: boolean;
    /**
     * Tells other tabs that this user logged out, without affecting this tab -
     * for callers that are already navigating this tab away (e.g. a real logout link)
     * and only need the other tabs to follow.
     */
    notifyLogout: () => void;
};

export const sessionActivityContextDefaultValue: SessionActivityContextValue = {
    heartbeat: () => {},
    isPrompted: false,
    getRemainingTime: () => 0,
    isEnabled: false,
    notifyLogout: () => {},
};

/**
 * React context exposing cross-tab idle-detection state and controls.
 * See SessionActivityProvider.
 */
export const SessionActivityContext = createContext<SessionActivityContextValue>(sessionActivityContextDefaultValue);

/**
 * React hook for reading/resetting cross-tab idle-detection state.
 * Returns safe no-op defaults when no SessionActivityProvider is mounted
 * (e.g. the feature is disabled via config).
 *
 * Usage:
 * ```
 * const { heartbeat } = useSessionActivity();
 *
 * return <Button onClick={heartbeat}>I'm still here</Button>;
 * ```
 */
export const useSessionActivity = (): SessionActivityContextValue => {
    return useContext(SessionActivityContext);
};
